import { BRAND_LOGO_URL, SITE_NAME, SITE_URL } from "@/constants/site";

export interface JsonLdFaq {
  question: string;
  answer: string;
}

export interface JsonLdPlan {
  name: string;
  price: string;
  url: string;
  description?: string;
  image?: string;
  category?: string;
  /** Código único do produto (SKU) usado no schema.org. Se ausente, é gerado a partir do nome. */
  sku?: string;
  /** Data (AAAA-MM-DD) até quando o preço é válido. Padrão: fim do próximo ano. */
  priceValidUntil?: string;
}

export interface JsonLdBreadcrumb {
  name: string;
  path: string;
}

export interface JsonLdArticle {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  section: string;
}

export interface JsonLdInput {
  title: string;
  description: string;
  url: string;
  image: string;
  titleEn?: string;
  titleEs?: string;
  descriptionEn?: string;
  descriptionEs?: string;
  faqs?: JsonLdFaq[];
  plans?: JsonLdPlan[];
  breadcrumbs?: JsonLdBreadcrumb[];
  article?: JsonLdArticle;
}

const SCRIPT_ID = "nx-jsonld";

/**
 * Normaliza preços no formato brasileiro (ex.: "1.259,90") para um número válido
 * aceito pelo schema.org/Google (ex.: "1259.90"). Sem isso o validador acusa
 * "número de ponto flutuante inválido na propriedade price".
 */
function normalizePrice(price: string): string {
  const cleaned = price.replace(/[^\d.,]/g, "");
  if (!cleaned) return price;
  const normalized = cleaned.replace(/\./g, "").replace(/,/g, ".");
  const value = Number(normalized);
  return Number.isFinite(value) ? value.toString() : price;
}

/**
 * Gera um SKU legível e estável a partir do nome do plano quando ele não é informado.
 * Ex.: "VPS BR SSD 16GB" -> "NX-VPS-BR-SSD-16GB".
 */
function buildSku(name: string): string {
  const slug = name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return `NX-${slug}`;
}

/** Data padrão de validade do preço: fim do próximo ano, sempre no futuro. */
const PRICE_VALID_UNTIL = `${new Date().getFullYear() + 1}-12-31`;

function buildGraph(input: JsonLdInput): Record<string, unknown> {
  const { title, description, url, image, titleEn, titleEs, descriptionEn, descriptionEs } = input;
  const { faqs, plans, breadcrumbs, article } = input;

  const nodes: Record<string, unknown>[] = [
    {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: BRAND_LOGO_URL,
      image,
      areaServed: "BR",
      sameAs: [url],
    },
    {
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
      inLanguage: "pt-BR",
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/blog/busca?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "WebPage",
      name: title,
      description,
      url,
      inLanguage: ["pt-BR", "en", "es"],
      alternateName: [titleEn, titleEs].filter(Boolean),
      descriptionAlt: [descriptionEn, descriptionEs].filter(Boolean),
    },
  ];

  if (article) {
    nodes.push({
      "@type": "BlogPosting",
      headline: article.headline,
      description: article.description,
      image: article.image,
      datePublished: article.datePublished,
      dateModified: article.dateModified,
      articleSection: article.section,
      inLanguage: "pt-BR",
      url,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: { "@type": "Organization", name: article.author, url: SITE_URL },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
        logo: { "@type": "ImageObject", url: BRAND_LOGO_URL },
      },
    });
  }

  if (plans && plans.length > 0) {
    nodes.push({
      "@type": "ItemList",
      name: title,
      itemListElement: plans.map((plan, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Product",
          name: `${plan.name} — ${SITE_NAME}`,
          description:
            plan.description ??
            `${plan.name} — plano ${SITE_NAME} com infraestrutura rápida, segura e suporte especializado.`,
          image: plan.image ?? image,
          category: plan.category,
          sku: plan.sku ?? buildSku(plan.name),
          brand: { "@type": "Brand", name: SITE_NAME },
          offers: {
            "@type": "Offer",
            price: normalizePrice(plan.price),
            priceCurrency: "BRL",
            url: plan.url,
            availability: "https://schema.org/InStock",
            priceValidUntil: plan.priceValidUntil ?? PRICE_VALID_UNTIL,
          },
        },
      })),
    });
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    nodes.push({
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.name,
        item: `${SITE_URL}${crumb.path === "/" ? "/" : crumb.path}`,
      })),
    });
  }

  if (faqs && faqs.length > 0) {
    nodes.push({
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": nodes,
  };
}

/**
 * Injeta (ou atualiza de forma idempotente) os dados estruturados JSON-LD da página atual.
 * Módulo isolado para não misturar a geração de dados estruturados com a mutação de
 * meta tags Open Graph / Twitter do gerenciador de head.
 */
export function applyJsonLd(input: JsonLdInput): void {
  let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(buildGraph(input));
}